/**
 * Position Metrics Service
 * Centralized service for calculating position and portfolio metrics
 */

import {
  calculateDTE,
  getDTEBucket,
  parseOptionSymbol,
} from "@/utils/option-symbol-utils";
import { greeksCacheServiceClient } from "@/services/greeks-cache-service-client";
import { underlyingPriceService } from "@/services/underlying-price-service";
import { sectorMappingService } from "@/services/sector-mapping-service";
import { quotesCacheService } from "@/services/quotes-cache-service";
import { getApiBaseUrl } from "@/lib/http-utils";
import { logger } from "@/lib/logger";
import { includePositionInActiveAllocation } from "@/lib/position-intents";
import type {
  Position,
  EnhancedPosition,
  PortfolioMetrics,
  GreeksData,
  SectorAllocation,
} from "@/types/position.types";

export type { PortfolioMetrics } from "@/types/position.types";

export interface PositionAttributeEntry {
  attribute_type: string;
  attribute_value: unknown;
}

interface PositionAttributeUpdate {
  account_number: string;
  symbol: string;
  attributes: {
    dte: number | null;
    dte_bucket: number | null;
    position_delta: number;
    notional: number;
    theta_decay: number;
    calculated_at: Date;
  };
}

const toBooleanLike = (value: unknown): boolean | null => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.length) {
      return null;
    }
    if (/^(true|1|yes)$/i.test(trimmed)) {
      return true;
    }
    if (/^(false|0|no)$/i.test(trimmed)) {
      return false;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === "boolean" ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

class PositionMetricsService {
  private cache = new Map<
    string,
    { data: EnhancedPosition; timestamp: number }
  >();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Enhance positions with calculated metrics
   */
  async enhancePositions(positions: Position[]): Promise<EnhancedPosition[]> {
    // Build a map of current prices from position data
    const currentPrices = new Map<string, number>();

    // Extract prices from stock positions and check for real-time quotes
    positions.forEach((position) => {
      if (position["instrument-type"] === "Equity") {
        const symbol = position.symbol;

        // First check for real-time quote
        const quote = quotesCacheService.getQuote(symbol);
        if (quote && quote.midPrice && quote.midPrice > 0) {
          currentPrices.set(symbol, quote.midPrice);
        } else {
          // Fall back to close price from position
          const closePrice = parseFloat(
            position["close-price"]?.toString() || "0",
          );
          const quantity = Math.abs(position.quantity || 1); // Use absolute value to handle short positions

          if (closePrice > 0 && quantity > 0) {
            // For stocks, we can derive the price per share
            const pricePerShare = closePrice;
            currentPrices.set(symbol, pricePerShare);
          }
        }
      }
    });

    // Batch fetch all required data
    const optionSymbols = positions
      .filter((p) => p["instrument-type"] === "Equity Option")
      .map((p) => p.symbol);

    const underlyingSymbols = [
      ...new Set(positions.map((p) => p["underlying-symbol"])),
    ];

    // Parallel fetch Greeks, prices, and sector data
    const [greeksData, priceData, sectorData] = await Promise.all([
      greeksCacheServiceClient.getGreeksBatch(optionSymbols),
      this.fetchUnderlyingPrices(underlyingSymbols),
      sectorMappingService.getSectorInfoBatch(underlyingSymbols),
    ]);

    // Merge current prices into price data (prefer actual position prices)
    currentPrices.forEach((price, symbol) => {
      priceData.set(symbol, price);
    });

    // Enhance each position
    const enhanced = positions.map((position) => {
      const cacheKey = `${position["account-number"]}_${position.symbol}`;
      const cached = this.cache.get(cacheKey);

      // Return cached if still fresh
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.data;
      }

      // Get sector info for this position
      const sectorInfo = sectorData.get(position["underlying-symbol"]);

      // Quiet: do not log missing/uncategorized sector information

      // Calculate metrics
      const enhancedPosition = this.calculatePositionMetrics(
        position,
        greeksData.get(position.symbol),
        priceData.get(position["underlying-symbol"]),
        sectorInfo,
      );

      // Cache the result
      this.cache.set(cacheKey, {
        data: enhancedPosition,
        timestamp: Date.now(),
      });

      return enhancedPosition;
    });

    // Store enhanced data in database for historical tracking
    await this.storeMetricsSnapshot(enhanced);

    return enhanced;
  }

  /**
   * Calculate all metrics for a single position
   */
  private calculatePositionMetrics(
    position: Position,
    greeks?: GreeksData,
    underlyingPrice?: number,
    sectorInfo?: {
      sector: string;
      assetType: "EQUITY" | "ETF" | "INDEX" | "FUTURE";
    },
  ): EnhancedPosition {
    const multiplier =
      position.multiplier ||
      (position["instrument-type"] === "Equity Option" ? 100 : 1);

    // Calculate delta
    const delta = this.calculateDelta(position, greeks);

    // Position-level Greeks
    const positionDelta = delta * position.quantity * multiplier;
    const positionTheta = (greeks?.theta || 0) * position.quantity * multiplier;
    const positionGamma = (greeks?.gamma || 0) * position.quantity * multiplier;
    const positionVega = (greeks?.vega || 0) * position.quantity * multiplier;

    // Notional value calculation - prefer passed underlying price, then check if we have price from positions
    let effectiveUnderlying = underlyingPrice;

    if (!effectiveUnderlying) {
      // Try to get price from position's close price if it's a stock
      if (position["instrument-type"] === "Equity" && position["close-price"]) {
        effectiveUnderlying = parseFloat(
          position["close-price"]?.toString() || "0",
        );
      } else {
        // No price available - notional will be 0
        effectiveUnderlying = 0;
      }
    }

    // Calculate notional based on instrument type
    let notional = 0;
    if (position["instrument-type"] === "Equity") {
      // For stocks: notional = quantity × price
      notional = Math.abs(position.quantity * effectiveUnderlying);
    } else if (position["instrument-type"] === "Equity Option") {
      // For options: notional = position delta × underlying price
      notional = Math.abs(positionDelta * effectiveUnderlying);
    }

    // DTE calculation for options
    let dte: number | null = null;
    let dteBucket: number | null = null;

      if (position["instrument-type"] === "Equity Option") {
      dte = calculateDTE(position.symbol);
      if (dte !== null) {
        dteBucket = getDTEBucket(dte);
      }
    }

    // Calculate market value from close price, quantity, and multiplier
    // (The API doesn't provide a pre-calculated market-value field)
    const closePrice = parseFloat(position["close-price"]?.toString() || "0");
    const quantity = position.quantity || 0;
    const marketValue = closePrice * quantity * multiplier;

    return {
      ...position,
      "market-value": marketValue, // Ensure market-value is set
      delta,
      positionDelta,
      notional,
      dte,
      dteBucket,
      thetaDecay: positionTheta,
      gammaRisk: positionGamma * effectiveUnderlying * 0.01, // 1% move impact
      vegaExposure: positionVega,
      calculatedAt: new Date(),
      underlyingPrice: effectiveUnderlying || null,
      sector: sectorInfo?.sector || "Uncategorized",
      assetType: sectorInfo?.assetType,
    };
  }

  /**
   * Calculate delta based on instrument type and available data
   */
  private calculateDelta(position: Position, greeks?: GreeksData): number {
    switch (position["instrument-type"]) {
      case "Equity":
        // Stocks have delta of 1 (long) or -1 (short)
        return position.quantity > 0 ? 1.0 : -1.0;

      case "Equity Option":
        if (greeks?.delta !== undefined) {
          return greeks.delta;
        }
        // Fallback: estimate based on moneyness if Greeks unavailable
        return this.estimateOptionDelta(position);

      default:
        return 0;
    }
  }

  /**
   * Estimate option delta when Greeks are unavailable
   */
  private estimateOptionDelta(position: Position): number {
    const parsed = parseOptionSymbol(position.symbol);
    if (!parsed) return 0.5; // Default fallback

    // Quiet: skip logging when Greeks are missing; fallback estimation is expected

    // For now, return a conservative estimate
    // TODO: Implement proper Black-Scholes or moneyness-based estimation
    const isCall = parsed.optionType === "C";

    // Option delta estimates (these are per-contract deltas):
    // Call options: positive delta (0 to 1)
    // Put options: negative delta (-1 to 0)
    // We'll use 0.5 and -0.5 as ATM estimates
    const optionDelta = isCall ? 0.5 : -0.5;

    // Return the per-contract delta
    // The position delta will be calculated as: delta × quantity × multiplier
    return optionDelta;
  }

  /**
   * Calculate portfolio-level metrics
   */
  async calculatePortfolioMetrics(
    enhancedPositions: EnhancedPosition[],
    accountNetLiquidatingValue?: number,
    positionAttributes?: Map<string, PositionAttributeEntry[]>,
  ): Promise<PortfolioMetrics> {
    const metrics: PortfolioMetrics = {
      totalValue: 0,
      totalDelta: 0,
      totalNotional: 0,
      totalTheta: 0,
      dteDistribution: new Map(),
      sectorExposure: new Map(),
      equitySectorAllocations: [],
      nonEquitySectorAllocations: [],
      calculatedAt: new Date(),
      accountNetLiquidatingValue,
    };

    // Aggregate metrics
    logger.debug(
      `Portfolio Metrics: Processing ${enhancedPositions.length} enhanced positions`,
    );

    const activeEnhancedPositions = enhancedPositions.filter((position) =>
      includePositionInActiveAllocation(position as unknown as Record<string, unknown>),
    );

    for (const position of activeEnhancedPositions) {
      metrics.totalValue += parseFloat(
        position["market-value"]?.toString() || "0",
      );
      metrics.totalDelta += position.positionDelta;
      metrics.totalNotional += position.notional;
      metrics.totalTheta += position.thetaDecay;

      // DTE distribution (options only, exclude long-term positions)
      if (position.dteBucket !== null) {
        // Check if position is marked as long-term
        const positionKey = `${position["account-number"]}_${position.symbol}`;
        const attributes = positionAttributes?.get(positionKey) ?? [];
        let isLongTerm = false;

        logger.debug(`Portfolio Metrics: Checking attributes for ${positionKey}`);

        const longTermAttr = attributes.find(
          (attr) => attr.attribute_type === "is_long_term",
        );
        if (longTermAttr) {
          const normalized = toBooleanLike(longTermAttr.attribute_value);
          if (normalized !== null) {
            isLongTerm = normalized;
            logger.debug(
              `Portfolio Metrics: Position ${position.symbol} long-term status: ${isLongTerm}`,
            );
          } else {
            logger.debug(
              `Portfolio Metrics: Unable to interpret long-term attribute for ${position.symbol}`,
            );
          }
        }

        // Skip long-term positions from DTE distribution
        if (isLongTerm === true) {
          logger.debug(
            `Portfolio Metrics: Skipping long-term position ${position.symbol} from DTE distribution, value: ${position["market-value"]}`,
          );
          continue;
        }

        logger.debug(
          `Portfolio Metrics: Adding position ${position.symbol} to bucket ${position.dteBucket}, market value: ${position["market-value"]}`,
        );

        const bucket = metrics.dteDistribution.get(position.dteBucket) || {
          count: 0,
          value: 0,
          percentage: 0,
        };

        bucket.count += 1;
        // Use the position's market value (net liq) directly
        const marketValue = Math.abs(
          parseFloat(position["market-value"]?.toString() || "0"),
        );
        bucket.value += marketValue;
        logger.debug(
          `Portfolio Metrics: Bucket ${position.dteBucket} updated - count: ${bucket.count}, value: ${bucket.value}`,
        );

        metrics.dteDistribution.set(position.dteBucket, bucket);
      }

      // Sector exposure - now using actual sector data
      const sector = position.sector || "Uncategorized";
      const exposure = metrics.sectorExposure.get(sector) || 0;
      metrics.sectorExposure.set(sector, exposure + position.notional);
    }

    // Calculate percentages for DTE distribution
    // Use account net liquidating value if provided, otherwise fall back to total options value
    const denominatorValue =
      accountNetLiquidatingValue || metrics.totalValue || 1;

    const totalOptionsValue = Array.from(
      metrics.dteDistribution.values(),
    ).reduce((sum, bucket) => sum + bucket.value, 0);

    logger.debug(
      `Portfolio Metrics: Total options value (non-long-term): ${totalOptionsValue}`,
    );
    logger.debug(
      `Portfolio Metrics: Account net liquidating value: ${accountNetLiquidatingValue}`,
    );
    logger.debug(`Portfolio Metrics: Using denominator: ${denominatorValue}`);
    logger.debug(
      `Portfolio Metrics: Total portfolio value: ${metrics.totalValue}`,
    );
    logger.debug(
      `Portfolio Metrics: DTE distribution size: ${metrics.dteDistribution.size}`,
    );

    for (const [bucket, data] of metrics.dteDistribution) {
      // Calculate percentage based on total account value
      data.percentage = (data.value / denominatorValue) * 100;
      logger.debug(
        `Portfolio Metrics: Bucket ${bucket} -> ${JSON.stringify(data)}`,
      );
    }

    // Calculate sector allocations
    const sectorDetails = new Map<
      string,
      {
        positions: EnhancedPosition[];
        notional: number;
        marketValue: number;
        delta: number;
      }
    >();

    // Group positions by sector
    for (const position of activeEnhancedPositions) {
      const sector = position.sector || "Uncategorized";
      if (!sectorDetails.has(sector)) {
        sectorDetails.set(sector, {
          positions: [],
          notional: 0,
          marketValue: 0,
          delta: 0,
        });
      }

      const detail = sectorDetails.get(sector)!;
      detail.positions.push(position);
      detail.notional += position.notional;
      detail.marketValue += parseFloat(
        position["market-value"]?.toString() || "0",
      );
      detail.delta += position.positionDelta;
    }

    // Convert to allocations - use market value (net liquidation value) for percentages
    const totalMarketValue = metrics.totalValue || 1; // Avoid division by zero

    for (const [sector, detail] of sectorDetails) {
      const allocation: SectorAllocation = {
        sector,
        positionCount: detail.positions.length,
        notionalValue: detail.notional,
        marketValue: detail.marketValue,
        percentageOfPortfolio: (detail.marketValue / totalMarketValue) * 100,
        delta: detail.delta,
        isEquity:
          sectorMappingService.getSectorClassification(sector) === "equity",
      };

      if (allocation.isEquity) {
        metrics.equitySectorAllocations.push(allocation);
      } else {
        metrics.nonEquitySectorAllocations.push(allocation);
      }
    }

    // Sort allocations by percentage descending
    metrics.equitySectorAllocations.sort(
      (a, b) => b.percentageOfPortfolio - a.percentageOfPortfolio,
    );
    metrics.nonEquitySectorAllocations.sort(
      (a, b) => b.percentageOfPortfolio - a.percentageOfPortfolio,
    );

    return metrics;
  }

  /**
   * Store metrics snapshot for historical tracking
   */
  private async storeMetricsSnapshot(
    positions: EnhancedPosition[],
  ): Promise<void> {
    // Store in position_attributes for persistence
    const updates = positions.reduce<PositionAttributeUpdate[]>((acc, position) => {
      const accountNumber = position["account-number"];
      const symbol = position.symbol;

      if (typeof accountNumber !== "string" || accountNumber.trim() === "") {
        return acc;
      }
      if (typeof symbol !== "string" || symbol.trim() === "") {
        return acc;
      }

      acc.push({
        account_number: accountNumber,
        symbol,
        attributes: {
          dte: position.dte,
          dte_bucket: position.dteBucket,
          position_delta: position.positionDelta,
          notional: position.notional,
          theta_decay: position.thetaDecay,
          calculated_at: position.calculatedAt,
        },
      });

      return acc;
    }, []);

    // Batch update position attributes
    await this.batchUpdatePositionAttributes(updates);
  }

  /**
   * Batch update position attributes
   */
  private async batchUpdatePositionAttributes(
    updates: PositionAttributeUpdate[],
  ): Promise<void> {
    try {
      const base = getApiBaseUrl();
      const response = await fetch(`${base}/api/market-data/position-metrics`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: 'include' as RequestCredentials,
        body: JSON.stringify({
          action: "saveMetrics",
          data: updates,
        }),
      });

      if (!response.ok) {
        logger.error(
          "Failed to save position metrics:",
          await response.text(),
        );
      }
    } catch (error) {
      logger.error("Error saving position metrics:", error);
    }
  }

  /**
   * Fetch underlying prices for multiple symbols
   */
  private async fetchUnderlyingPrices(
    symbols: string[],
  ): Promise<Map<string, number>> {
    // Use the batch method from underlying price service
    // This will check quotes cache first, then REST API
    const prices = await underlyingPriceService.getUnderlyingPrices(symbols);

    // Log which symbols we couldn't get prices for
    for (const symbol of symbols) {
      if (!prices.has(symbol)) {
        logger.debug(
          `Position Metrics: Could not get price for ${symbol} from WebSocket or API`,
        );
      }
    }

    return prices;
  }

  /**
   * Clear cache (useful for testing or manual refresh)
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const positionMetricsService = new PositionMetricsService();
